/**
 * Tab Filtering Utilities
 *
 * Provides fuzzy search filtering for tabs in the Recent Tabs section.
 */

import type { Tab, WorkspaceType } from '../../core/types/workspaceTypes';
import type { StorageResourceRef } from '../../../../types/azureStorage';
import type { TabHistoryEntry } from '../../../tab-history/types/tabHistoryTypes';
import { safeTabConnectionInfo, safeTabTitle } from '../../core/store/safeTabText';

/**
 * Unified tab item for display in RecentTabs section.
 * Normalizes both open tabs and closed tabs to a common structure.
 */
export type RecentTabItem = {
  /** Original tab/entry ID */
  id: string;
  /** Display title */
  title: string;
  /** Workspace type */
  type: WorkspaceType;
  /** Whether this tab is currently open */
  isOpen: boolean;
  /** Connection info for display */
  connectionName?: string;
  connectionId?: string;
  /** When the tab was closed (only for closed tabs) */
  closedAt?: Date;
  /** Subtitle/description based on tab type */
  subtitle: string;
  /** Original tab (for open tabs) */
  originalTab?: Tab;
  /** Original history entry (for closed tabs) */
  originalEntry?: TabHistoryEntry;
};

/** Round B U31: a storage tab's kind in words, never the internal `storage-explorer` id. */
export function storageTabKindLabel(root: StorageResourceRef | undefined): string {
  switch (root?.kind) {
    case 'container':
    case 'blobPrefix':
    case 'blob':
      return 'Blob container';
    case 'table':
    case 'entity':
      return 'Table';
    case 'queue':
    case 'message':
      return 'Queue';
    case 'share':
    case 'directory':
    case 'file':
      return 'File share';
    case 'hnsPath':
      return 'Data Lake';
    default:
      return 'Azure Storage';
  }
}

/**
 * Get subtitle text based on tab type and content.
 */
function getTabSubtitle(tab: Tab): string {
  switch (tab.type) {
    case 'query':
      return tab.content.query
        ? truncateText(tab.content.query, 50)
        : '(empty query)';
    case 'table-explorer':
      return tab.content.tableName
        ? `${tab.content.schema}.${tab.content.tableName}`
        : '(no table)';
    case 'table-designer':
      return tab.content.tableName
        ? `${tab.content.schema}.${tab.content.tableName}`
        : '(new table)';
    case 'plan':
      return tab.content.filePath
        ? getFileNameFromPath(tab.content.filePath)
        : '(no plan file)';
    case 'security':
      return tab.content.objectType ?? 'Security Manager';
    case 'schema-compare':
      return 'Schema Compare';
    case 'data-compare':
      return 'Data Compare';
    case 'dependency-viewer':
      return tab.content.objectName
        ? `${tab.content.schema}.${tab.content.objectName}`
        : 'Dependency Viewer';
    case 'schema-overview':
      return tab.content.databaseName || 'Schema Overview';
    case 'spatial-explorer':
      return tab.content.databaseName || 'Spatial Explorer';
    case 'table-editor':
      return tab.content.tableName
        ? `${tab.content.schema}.${tab.content.tableName}`
        : '(no table)';
    case 'start-page':
      return 'Welcome';
    case 'storage-explorer':
      return storageTabKindLabel(tab.content.root);
    case 'plsql-editor':
      return tab.content.objectName
        ? `${tab.content.schema}.${tab.content.objectName}`
        : 'PL/SQL Editor';
    case 'pl-sql-debugger':
      return tab.content.objectName
        ? `${tab.content.schema}.${tab.content.objectName}`
        : 'PL/SQL Debugger';
    default:
      return '';
  }
}

/**
 * Get subtitle text for a closed tab history entry.
 */
function getHistorySubtitle(entry: TabHistoryEntry): string {
  switch (entry.type) {
    case 'query':
      return entry.query ? truncateText(entry.query, 50) : '(empty query)';
    case 'table-explorer':
      return entry.tableName
        ? `${entry.schema ?? 'dbo'}.${entry.tableName}`
        : '(no table)';
    case 'plan':
      return entry.filePath
        ? getFileNameFromPath(entry.filePath)
        : '(no plan file)';
    case 'storage-explorer':
      return 'Azure Storage';
    default:
      return entry.type;
  }
}

/**
 * Truncate text for display.
 */
function truncateText(text: string, maxLength = 50): string {
  const trimmed = text.trim().split('\n')[0];
  if (trimmed.length <= maxLength) return trimmed;
  return trimmed.slice(0, maxLength - 3) + '...';
}

/**
 * Get filename from a file path.
 */
function getFileNameFromPath(filePath: string): string {
  const parts = filePath.split(/[/\\]/);
  return parts[parts.length - 1] || filePath;
}

/**
 * Convert open tab to RecentTabItem.
 */
export function tabToRecentItem(tab: Tab): RecentTabItem {
  return {
    id: tab.id,
    title: safeTabTitle(tab.title),
    type: tab.type,
    isOpen: true,
    connectionName: safeTabConnectionInfo(tab.connectionInfo)?.connectionName,
    connectionId: tab.connectionInfo?.connectionId,
    subtitle: getTabSubtitle(tab),
    originalTab: tab,
  };
}

/**
 * Convert closed tab history entry to RecentTabItem.
 */
export function historyEntryToRecentItem(entry: TabHistoryEntry): RecentTabItem {
  return {
    id: entry.id,
    title: safeTabTitle(entry.title),
    type: entry.type,
    isOpen: false,
    connectionName: safeTabConnectionInfo(entry.connectionInfo)?.connectionName,
    connectionId: entry.connectionInfo?.connectionId,
    closedAt: entry.closedAt,
    subtitle: getHistorySubtitle(entry),
    originalEntry: entry,
  };
}

/**
 * Simple fuzzy search - checks if query terms appear in the text (case-insensitive).
 * Terms are matched in order but don't need to be adjacent.
 */
export function fuzzyMatch(text: string, query: string): boolean {
  if (!query.trim()) return true;

  const normalizedText = text.toLowerCase();
  const terms = query.toLowerCase().trim().split(/\s+/);

  // All terms must appear somewhere in the text
  return terms.every((term) => normalizedText.includes(term));
}

/**
 * Filter tab items by search query.
 * Matches against title, subtitle, and connection name.
 */
export function filterTabItems(
  items: RecentTabItem[],
  query: string
): RecentTabItem[] {
  if (!query.trim()) return items;

  return items.filter((item) => {
    const searchableText = [
      item.title,
      item.subtitle,
      item.connectionName ?? '',
      item.type,
    ].join(' ');

    return fuzzyMatch(searchableText, query);
  });
}

/**
 * Sort tab items: open tabs first, then by most recently closed.
 */
export function sortTabItems(items: RecentTabItem[]): RecentTabItem[] {
  return [...items].sort((a, b) => {
    // Open tabs first
    if (a.isOpen && !b.isOpen) return -1;
    if (!a.isOpen && b.isOpen) return 1;

    // For closed tabs, sort by closedAt (most recent first)
    if (!a.isOpen && !b.isOpen) {
      const aTime = a.closedAt?.getTime() ?? 0;
      const bTime = b.closedAt?.getTime() ?? 0;
      return bTime - aTime;
    }

    return 0;
  });
}
